// Display helpers for Ad Performance. Money is in the ad account's currency.

export const money = (
  v: number | null | undefined,
  currency = 'USD',
  digits?: number
): string => {
  if (v === null || v === undefined) return '–'
  const fractionDigits = digits ?? (Math.abs(v) < 10 ? 2 : 0)
  return new Intl.NumberFormat('en-US', {
    currency,
    maximumFractionDigits: fractionDigits,
    minimumFractionDigits: fractionDigits,
    style: 'currency',
  }).format(v)
}

export const compact = (v: number | null | undefined): string => {
  if (v === null || v === undefined) return '–'
  if (Math.abs(v) < 10000) return new Intl.NumberFormat('en-US').format(v)
  return new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 1,
    notation: 'compact',
  }).format(v)
}

export const pct = (v: number | null | undefined, digits = 2): string =>
  v === null || v === undefined ? '–' : `${v.toFixed(digits)}%`

// Meta placement keys are "<publisher_platform>:<platform_position>".
const PLACEMENT_LABELS: Record<string, string> = {
  'audience_network:an_classic': 'Audience Network',
  'audience_network:rewarded_video': 'Audience Network video',
  'facebook:facebook_reels': 'Facebook Reels',
  'facebook:facebook_reels_overlay': 'Facebook Reels overlay',
  'facebook:facebook_stories': 'Facebook Stories',
  'facebook:feed': 'Facebook Feed',
  'facebook:instream_video': 'Facebook in-stream video',
  'facebook:marketplace': 'Marketplace',
  'facebook:search': 'Facebook Search',
  'instagram:feed': 'Instagram Feed',
  'instagram:instagram_explore': 'Instagram Explore',
  'instagram:instagram_reels': 'Instagram Reels',
  'instagram:instagram_stories': 'Instagram Stories',
  'messenger:messenger_inbox': 'Messenger',
  'threads:threads_feed': 'Threads',
}

export const placementLabel = (key: string): string =>
  PLACEMENT_LABELS[key] ??
  key
    .split(':')
    .pop()!
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())

const GENDER_LABELS: Record<string, string> = {
  female: 'Female',
  male: 'Male',
  unknown: 'Unknown',
}

export const genderLabel = (key: string): string => GENDER_LABELS[key] ?? key

const OBJECTIVES: Record<string, string> = {
  // Includes legacy (pre-2022) objectives still returned for old campaigns.
  BRAND_AWARENESS: 'Awareness',
  CONVERSIONS: 'Sales',
  LEAD_GENERATION: 'Leads',
  LINK_CLICKS: 'Traffic',
  MESSAGES: 'Engagement',
  OUTCOME_APP_PROMOTION: 'App promotion',
  OUTCOME_AWARENESS: 'Awareness',
  OUTCOME_ENGAGEMENT: 'Engagement',
  OUTCOME_LEADS: 'Leads',
  OUTCOME_SALES: 'Sales',
  OUTCOME_TRAFFIC: 'Traffic',
  PAGE_LIKES: 'Engagement',
  POST_ENGAGEMENT: 'Engagement',
  REACH: 'Awareness',
  VIDEO_VIEWS: 'Engagement',
}

export const objectiveLabel = (key: string | null): string =>
  key ? OBJECTIVES[key] ?? key : '–'

// A reach/awareness campaign's "result" is impressions, not an action, so it
// is left out when results are added up across campaigns.
export const actionResults = (c: {
  result_type: string | null
  results: number | null
}): number => (c.result_type === 'impressions' ? 0 : c.results ?? 0)

const RESULT_TYPES: Record<string, string> = {
  app_installs: 'app installs',
  impressions: 'impressions',
  leads: 'leads',
  link_clicks: 'link clicks',
  post_engagement: 'engagements',
  purchases: 'purchases',
}

export const resultTypeLabel = (key: string | null): string =>
  key ? RESULT_TYPES[key] ?? key : ''
